/**
 * Avisos da plataforma na tela do cliente.
 *
 * Busca o que deve aparecer na rota aberta, conta a visualização uma vez por
 * aviso, e expõe dispensar/clicar. O aviso chega na hora pelo WebSocket
 * (`communication:new`) e também a cada troca de tela.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";
import { useAdminWS } from "../../lib/adminWS";

const Ctx = createContext({
  avisos: [],
  dispensar: () => {},
  clicar: () => {},
  responder: () => {},
  marcarVideo: () => {},
});

export const useAvisos = () => useContext(Ctx);

export function CommunicationsProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [avisos, setAvisos] = useState([]);
  const vistos = useRef(new Set());
  // Regra "tempo na tela": conta desde que esta rota abriu.
  const entrouEm = useRef(Date.now());
  const recheque = useRef(null);
  // Guarda a última versão de `carregar`: handlers e timers agendados não podem
  // disparar a busca da rota antiga.
  const carregarRef = useRef(null);

  const ativo = Boolean(user?.client_id) && !location.pathname.startsWith("/admin");

  const carregar = useCallback(async () => {
    if (!ativo) {
      setAvisos([]);
      return;
    }
    try {
      const { data } = await api.get("/communications/active", {
        params: {
          path: location.pathname,
          segundos: Math.round((Date.now() - entrouEm.current) / 1000),
          plataforma: "web",
        },
      });
      setAvisos(data.items || []);
      // O servidor avisa quando uma regra de tempo na tela vai passar a valer:
      // uma única consulta extra na hora certa, em vez de ficar perguntando.
      clearTimeout(recheque.current);
      const faltam = Number(data.proxima_checagem_em || 0);
      if (faltam > 0) {
        recheque.current = setTimeout(() => carregarRef.current(), (faltam + 1) * 1000);
      }
    } catch {
      setAvisos([]);
    }
  }, [ativo, location.pathname]);

  carregarRef.current = carregar;

  useEffect(() => {
    entrouEm.current = Date.now();
    return () => clearTimeout(recheque.current);
  }, [location.pathname]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Campanha agendada entra no ar sozinha: sem isso o usuário só veria o
  // aviso ao trocar de tela.
  useEffect(() => {
    if (!ativo) return undefined;
    const t = setInterval(carregar, 120000);
    return () => clearInterval(t);
  }, [ativo, carregar]);

  // `useAdminWS` guarda o handler da primeira renderização: sem o ref, o
  // push chegaria pedindo os avisos da rota antiga.
  useAdminWS(
    "communication:new",
    useCallback(() => {
      // Espera de 5 a 12s: dentro de um mesmo cliente, dezenas de pessoas
      // recebem o push no mesmo instante — sem isso, todas chamariam a API junto.
      const espera = 5000 + Math.random() * 7000;
      setTimeout(() => carregarRef.current(), espera);
    }, []),
  );

  // Visualização conta uma vez por aviso por sessão do navegador.
  useEffect(() => {
    avisos.forEach((a) => {
      if (vistos.current.has(a.id)) return;
      vistos.current.add(a.id);
      api.post(`/communications/${a.id}/event`, { tipo: "view" }).catch(() => {});
    });
  }, [avisos]);

  const dispensar = useCallback((aviso) => {
    setAvisos((l) => l.filter((a) => a.id !== aviso.id));
    api.post(`/communications/${aviso.id}/event`, { tipo: "dismiss" }).catch(() => {});
  }, []);

  const clicar = useCallback(
    (aviso) => {
      api.post(`/communications/${aviso.id}/event`, { tipo: "click" }).catch(() => {});
      const url = aviso.conteudo?.cta_url || "";
      setAvisos((l) => l.filter((a) => a.id !== aviso.id));
      if (!url) return;
      if (url.startsWith("/") && !aviso.conteudo?.cta_nova_aba) navigate(url);
      else window.open(url, "_blank", "noopener");
    },
    [navigate],
  );

  // Play do vídeo conta como clique, mas NÃO fecha o aviso (diferente do CTA).
  // Progresso do vídeo tem métrica própria: não mexe em cliques nem fecha o aviso.
  const marcarVideo = useCallback((aviso, payload) => {
    api.post(`/communications/${aviso.id}/video-event`, payload).catch(() => {});
  }, []);

  const responder = useCallback((aviso, nota, comentario) => {
    api
      .post(`/communications/${aviso.id}/answer`, { nota, comentario: comentario || "" })
      .catch(() => {});
  }, []);

  const valor = useMemo(
    () => ({ avisos, dispensar, clicar, responder, marcarVideo }),
    [avisos, dispensar, clicar, responder, marcarVideo],
  );
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}
